import { useId, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import { useModalFocus } from "@optimyl/ui";

interface ConfirmModalProps {
  title: string;
  message: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  confirmVariant?: "danger" | "primary";
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmModal({
  title,
  message,
  confirmLabel,
  cancelLabel = "Cancel",
  confirmVariant = "danger",
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  // Initial focus goes to Cancel — safer default for destructive confirms so the
  // user doesn't accidentally Enter-press into a destructive action.
  const cancelRef = useRef<HTMLButtonElement>(null);
  const containerRef = useModalFocus(onCancel, cancelRef);
  const titleId = useId();
  const descId = useId();

  const confirmClass =
    confirmVariant === "danger"
      ? "bg-danger text-white hover:bg-red-700"
      : "bg-brand-green text-white hover:bg-brand-green-dark";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onCancel}
    >
      <div
        ref={containerRef}
        className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 id={titleId} className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onCancel}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
        <p id={descId} className="text-sm text-gray-600 mb-6">{message}</p>
        <div className="grid grid-cols-2 gap-3">
          <button
            ref={cancelRef}
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 cursor-pointer min-h-[44px]"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm font-medium rounded-lg cursor-pointer min-h-[44px] ${confirmClass}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
